import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, Observable } from 'rxjs';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AdminService } from 'src/app/core/services/admin.service';
import { TenantService } from 'src/app/core/services/tenant.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { UnsavedChangesComponent } from 'src/app/shared/components/unsaved-changes/unsaved-changes.component';
import { ApiClient, ApiClientUpsert } from 'src/app/shared/models/integration.models';
import { EndpointPermission } from 'src/app/core/models/endpoint-permission';
import { IntegrationService } from 'src/app/shared/services/integration.service';

@Component({
  selector: 'app-api-client-modal',
  templateUrl: './api-client-modal.component.html',
  standalone: false,
  providers: [DialogService],
})
export class ApiClientModalComponent implements OnInit {
  editingId?: number;
  saving = false;
  /** Skeleton do modal enquanto os gets de abertura (tenants + permissions) não terminam. */
  initialLoading = true;
  tenants: { id: string; name: string }[] = [];
  permissionOptions: Array<{ label: string; value: string }> = [];
  generatedSecret = '';

  // Available/Selected permissions management
  availablePermissions: Array<{ label: string; value: string; selected?: boolean }> = [];
  selectedPermissions: Array<{ label: string; value: string; selected?: boolean }> = [];
  searchAvailable = '';
  searchSelected = '';

  form: FormGroup = this.fb.group({
    tenantId: ['', Validators.required],
    name: ['', Validators.required],
    validityDays: [365, [Validators.required, Validators.min(1), Validators.max(3650)]],
    allowedPermissionKeys: [[], Validators.required],
    enabled: [true],
  });

  private initialState = '';

  constructor(
    private readonly fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly integrationService: IntegrationService,
    private readonly toastService: AppToastService,
    private readonly dialogService: DialogService,
    private readonly tenantService: TenantService,
    private readonly adminService: AdminService,
  ) {}

  ngOnInit(): void {
    const client: ApiClient | undefined = this.config.data?.client;
    this.editingId = client?.id;

    this.form.reset({
      tenantId: client?.tenantId || '',
      name: client?.name || '',
      validityDays: client?.expiresAt ? this.getRemainingDays(client.expiresAt) : 365,
      allowedPermissionKeys: client?.allowedPermissionKeys || [],
      enabled: client?.enabled ?? true,
    });

    if (this.editingId) {
      this.form.get('tenantId')?.disable();
    }

    // Une os gets de abertura: o skeleton só some quando tenants E permissions terminarem.
    forkJoin({
      tenants: this.tenantService.getTenants(1000, 0, ''),
      permissions: this.adminService.getEndpointPermissions(),
    })
      .pipe(finalize(() => (this.initialLoading = false)))
      .subscribe({
        next: ({ tenants, permissions }) => {
          this.tenants = ((tenants as any)?.tenants || [])
            .map((tenant: any) => ({ id: tenant.id, name: tenant.name }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name));

          const sorted = (permissions || [])
            .filter((permission) => permission.isActive)
            .sort((a, b) => {
              const controllerCompare = a.controllerName.localeCompare(b.controllerName);
              return controllerCompare !== 0
                ? controllerCompare
                : a.permissionKey.localeCompare(b.permissionKey);
            })
            .map((permission) => ({
              label: `${permission.displayName} (${permission.permissionKey})`,
              value: permission.permissionKey,
            }));

          const currentSelected = this.form.get('allowedPermissionKeys')?.value || [];

          this.availablePermissions = sorted
            .filter(p => !currentSelected.includes(p.value))
            .map(p => ({ ...p, selected: false }));

          this.selectedPermissions = sorted
            .filter(p => currentSelected.includes(p.value))
            .map(p => ({ ...p, selected: false }));

          this.permissionOptions = sorted; // Keep for backward compat if needed
        },
      });

    this.takeSnapshot();
  }

  tryClose(): void {
    if (this.generatedSecret) {
      this.ref.close({ refresh: true });
      return;
    }

    if (this.hasChanges()) {
      const ref = this.dialogService.open(UnsavedChangesComponent, {
        showHeader: false,
        width: '450px',
        contentStyle: { padding: '0', borderRadius: '8px' },
        closable: false,
        data: { keepEditingLabel: this.editingId ? 'Keep editing' : 'Keep creating' },
      });

      ref.onClose.subscribe((discard: boolean) => {
        if (discard) this.ref.close();
      });
      return;
    }

    this.ref.close();
  }

  save(): void {
    if (this.generatedSecret) {
      this.ref.close({ refresh: true });
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const raw = this.form.getRawValue();
    const payload: ApiClientUpsert = {
      tenantId: raw.tenantId || null,
      name: String(raw.name || '').trim(),
      enabled: !!raw.enabled,
      validityDays: Number(raw.validityDays || 0),
      allowedPermissionKeys: (raw.allowedPermissionKeys || []).map((value: string) => String(value)),
    };

    const request$: Observable<ApiClient> = this.editingId
      ? this.integrationService.updateApiClient(this.editingId, payload)
      : this.integrationService.addApiClient(payload);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (response) => {
        if (this.editingId) {
          this.toastService.success('API client updated', 'API client saved successfully.');
          this.ref.close('success');
          return;
        }

        this.generatedSecret = response.generatedSecret || '';
        this.toastService.success('API client created', 'Copy the secret now. It will not be shown again.');
        this.takeSnapshot();
      },
    });
  }

  copySecret(): void {
    if (!this.generatedSecret) return;

    navigator.clipboard?.writeText(this.generatedSecret);
    this.toastService.success('Secret copied', 'The generated secret was copied to the clipboard.');
  }

  regenerating = false;

  regenerateKey(): void {
    if (!this.editingId) return;

    this.regenerating = true;
    this.integrationService
      .regenerateApiClientSecret(this.editingId)
      .pipe(finalize(() => (this.regenerating = false)))
      .subscribe({
        next: (response) => {
          this.generatedSecret = response.generatedSecret || '';
          this.toastService.success('Key regenerated', 'Copy the new secret now. It will not be shown again.');
        },
      });
  }

  // Permission list management
  filteredAvailable() {
    return this.availablePermissions.filter(p =>
      p.label.toLowerCase().includes(this.searchAvailable.toLowerCase())
    );
  }

  filteredSelected() {
    return this.selectedPermissions.filter(p =>
      p.label.toLowerCase().includes(this.searchSelected.toLowerCase())
    );
  }

  isAllSelected(list: any[]): boolean {
    return list.length > 0 && list.every(p => p.selected);
  }

  hasSelected(list: any[]): boolean {
    return list.some(p => p.selected);
  }

  toggleSelectAll(list: any[], event: any): void {
    list.forEach(p => p.selected = event.target.checked);
  }

  moveToSelected(): void {
    const toMove = this.availablePermissions.filter(p => p.selected);
    this.availablePermissions = this.availablePermissions.filter(p => !p.selected);
    toMove.forEach(p => p.selected = false);
    this.selectedPermissions.push(...toMove);
    this.syncFormValues();
  }

  moveToAvailable(): void {
    const toMove = this.selectedPermissions.filter(p => p.selected);
    this.selectedPermissions = this.selectedPermissions.filter(p => !p.selected);
    toMove.forEach(p => p.selected = false);
    this.availablePermissions.push(...toMove);
    this.syncFormValues();
  }

  private syncFormValues(): void {
    const selectedValues = this.selectedPermissions.map(p => p.value);
    this.form.patchValue({ allowedPermissionKeys: selectedValues });
  }

  private takeSnapshot(): void {
    setTimeout(() => {
      this.initialState = JSON.stringify(this.form.getRawValue());
    }, 100);
  }

  private hasChanges(): boolean {
    return JSON.stringify(this.form.getRawValue()) !== this.initialState;
  }

  private getRemainingDays(expiresAt: string): number {
    const expiresDate = new Date(expiresAt).getTime();
    const days = Math.ceil((expiresDate - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(1, days || 1);
  }
}
